/**
 * @fileoverview State and data of the Sales Performance screen.
 * @description Holds the selected period and team, reads the summary for them, and runs Export CSV,
 * so the page stays a thin layout. The team is sent only when one is chosen (a Manager or Executive
 * that sends it, even `ALL`, is refused), and the last export's note or refusal is cleared whenever
 * the selection changes because it described another selection (an export still in flight for an
 * earlier selection is dropped when it lands, for the same reason). The period and team options
 * come from the summary, so the last summary that loaded is kept for them: a failed read for a new
 * selection must not leave the user with nothing to switch back to.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useRef, useState } from 'react'

import { useExportSalesPerformance } from '@/features/sales/hooks/useExportSalesPerformance'
import { useSalesPerformanceSummary } from '@/features/sales/hooks/useSalesPerformance'
import { ALL_TEAMS, downloadCsv } from '@/features/sales/lib/sales-performance.utils'

import type {
  SalesPerformancePeriodCode,
  SalesPerformanceQuery,
  SalesPerformanceSummary,
} from '@/features/sales/types/sales-performance.types'

/**
 * @description The screen's selection, summary and export.
 * @returns {object} The selection, the query it makes, the summary query, the last summary that
 * loaded (for the filter options), the export mutation, the name of the last exported file, and the
 * handlers that change them.
 */
export function useSalesPerformanceScreen() {
  const [period, setPeriod] = useState<SalesPerformancePeriodCode>('FY')
  const [team, setTeam] = useState(ALL_TEAMS)
  const [exportedFile, setExportedFile] = useState<string | null>(null)
  const [lastSummary, setLastSummary] = useState<SalesPerformanceSummary | undefined>(undefined)
  const selectionRef = useRef<SalesPerformanceQuery>({ period: 'FY', team: undefined })
  const query: SalesPerformanceQuery = { period, team: team === ALL_TEAMS ? undefined : team }
  const summaryQuery = useSalesPerformanceSummary(query)
  const exportMutation = useExportSalesPerformance(
    /**
     * @description Saves the exported file and notes its name, unless the selection moved on while
     * it was being made.
     * @param {{ filename: string; csv: string }} file - The exported file.
     * @param {SalesPerformanceQuery} exported - The selection it was exported for.
     */
    (file, exported) => {
      const current = selectionRef.current
      if (exported.period !== current.period || exported.team !== current.team) return
      downloadCsv(file.filename, file.csv)
      setExportedFile(file.filename)
    },
  )

  if (summaryQuery.data && summaryQuery.data !== lastSummary) setLastSummary(summaryQuery.data)

  /**
   * @description Clears the last export's note or refusal.
   */
  const clearExportOutcome = () => {
    setExportedFile(null)
    exportMutation.reset()
  }

  /**
   * @description Applies a new period.
   * @param {SalesPerformancePeriodCode} next - The chosen period.
   */
  const changePeriod = (next: SalesPerformancePeriodCode) => {
    clearExportOutcome()
    selectionRef.current = { period: next, team: selectionRef.current.team }
    setPeriod(next)
  }

  /**
   * @description Applies a new team.
   * @param {string} next - The chosen team key, or `ALL`.
   */
  const changeTeam = (next: string) => {
    clearExportOutcome()
    selectionRef.current = {
      period: selectionRef.current.period,
      team: next === ALL_TEAMS ? undefined : next,
    }
    setTeam(next)
  }

  /**
   * @description Starts the export of the current selection.
   */
  const startExport = () => {
    setExportedFile(null)
    exportMutation.mutate(query)
  }

  return {
    period,
    team,
    query,
    summaryQuery,
    optionsSummary: summaryQuery.data ?? lastSummary,
    exportMutation,
    exportedFile,
    changePeriod,
    changeTeam,
    startExport,
  }
}
